import React, { useState } from 'react';
import { Box, Button, Typography, Stack, Alert, CircularProgress } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useComparisonSummary } from '../hooks';
import type { ComparisonSummaryRequest, LlmSection } from '../api';

interface AiSummaryPanelProps {
  /** Builds the request payload lazily, from the current on-screen data. */
  buildPayload: () => ComparisonSummaryRequest | Promise<ComparisonSummaryRequest>;
  /** Disable the button (e.g. while the underlying data is still loading). */
  disabled?: boolean;
  title?: string;
}

const SectionBlock: React.FC<{ section: LlmSection }> = ({ section }) => (
  <Box sx={{ mb: 1.5 }}>
    {section.title && section.title !== 'Answer' && (
      <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 0.5 }}>
        {section.title}
      </Typography>
    )}
    {section.items.length > 0 ? (
      <Stack component='ul' spacing={0.5} sx={{ pl: 2.5, m: 0 }}>
        {section.items.map((item, i) => (
          <Typography component='li' variant='body2' key={i}>
            {item}
          </Typography>
        ))}
      </Stack>
    ) : (
      section.text && <Typography variant='body2'>{section.text}</Typography>
    )}
  </Box>
);

/**
 * On-demand AI narrative of the current dashboard view. The user clicks
 * "Generate AI summary" — only then is OpenAI called (via useComparisonSummary)
 * with the exact metrics/groups currently on screen.
 */
export const AiSummaryPanel: React.FC<AiSummaryPanelProps> = ({
  buildPayload,
  disabled = false,
  title = 'AI Summary',
}) => {
  const summary = useComparisonSummary();
  const data = summary.data?.data;

  const [isFetching, setIsFetching] = useState(false);

  const handleGenerate = async () => {
    setIsFetching(true);
    try {
      const payload = await buildPayload();
      summary.mutate(payload);
    } finally {
      setIsFetching(false);
    }
  };

  const isLoading = isFetching || summary.isPending;

  const errorMessage =
    summary.error && typeof summary.error === 'object'
      ? (summary.error as { response?: { status?: number; data?: { message?: string } } }).response?.status === 501
        ? "AI summaries aren't configured for this environment."
        : (summary.error as { response?: { data?: { message?: string } } }).response?.data?.message ||
          "Couldn't generate the AI summary. Please try again."
      : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Button
          size='small'
          variant='contained'
          startIcon={isLoading ? <CircularProgress size={16} color='inherit' /> : <AutoAwesomeIcon />}
          disabled={disabled || isLoading}
          onClick={handleGenerate}
        >
          {isLoading ? 'Generating…' : data ? 'Regenerate' : 'Generate AI summary'}
        </Button>
      </Box>

      {errorMessage && (
        <Alert severity='info' sx={{ mt: 1.5 }}>
          {errorMessage}
        </Alert>
      )}

      {data && (
        <Box sx={{ mt: 1.5 }}>
          {data.summary_sections.length > 0 ? (
            data.summary_sections.map((section, i) => <SectionBlock key={i} section={section} />)
          ) : (
            <Typography variant='body2' sx={{ whiteSpace: 'pre-line' }}>
              {data.summary_text}
            </Typography>
          )}
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
            Generated by AI from wearable data patterns. Supportive only — not a substitute for medical assessment.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
